import { createContext, useContext } from 'react';
import type { ReactElement, ReactNode } from 'react';
import type { Capability, Role } from '@fleet/shared';
import { useSessionQuery } from '@/api/endpoints';
import type { Session } from '@/api/types';
import { SignInPage } from '@/features/auth/SignInPage';

/**
 * Who is using the app.
 *
 * The capability list comes from the server, alongside the role, so the back
 * office draws exactly what that account can reach. It is used to decide what
 * to show — never whether something is allowed. That is settled by the API on
 * every request, and the tests in backend/tests/test_fleet_api.py are what
 * prove it.
 */

const SessionContext = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): ReactElement {
  const { data, isLoading, isError, error } = useSessionQuery();

  if (isLoading) {
    return <div className="min-h-dvh bg-paper" aria-busy="true" />;
  }

  // A 401 is the ordinary case, not a failure: nobody is signed in yet.
  const status = isError && typeof error === 'object' && error !== null && 'status' in error
    ? (error as { status: number | string }).status
    : undefined;

  if (isError && (status === 401 || status === 403)) {
    return <SignInPage />;
  }

  if (isError || data === undefined) {
    return (
      <div className="min-h-dvh bg-paper p-6">
        <div className="max-w-lg border border-rule bg-panel p-5">
          <h1 className="text-heading font-semibold">The back office cannot reach the server</h1>
          <p className="mt-2 text-ink-muted">
            Check the connection and reload. Anything already written is safe — nothing is stored
            in this browser.
          </p>
        </div>
      </div>
    );
  }

  return <SessionContext.Provider value={data}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (session === null) throw new Error('useSession used outside SessionProvider');
  return session;
}

/** Should this be on screen? Never: is this allowed? */
export function useCan(capability: Capability): boolean {
  const session = useSession();
  return (session.capabilities as readonly string[]).includes(capability);
}

export function useRole(): Role {
  return useSession().role as Role;
}
